import { DataLayer, calcPrice } from "@grandvisionhq/analytics";
import { Mappers } from "@grandvisionhq/carts-v2";
import { mapOrderLineItemToDataLayerProductObjects } from "@grandvisionhq/fun-orders";
import { ExpressPaymentMethod } from "@grandvisionhq/payments";
import { monetate } from "@grandvisionhq/svc-monetate";
import { optionalKey } from "@grandvisionhq/utils";
const checkoutSteps = {
  [
    "login"
    /* Login */
  ]: 1,
  [
    "shipping"
    /* Shipping */
  ]: 2,
  [
    "overview"
    /* Overview */
  ]: 3,
  [
    "payment"
    /* Payment */
  ]: 4
};
const pushCheckoutEventToDataLayer = (cart, currency, checkoutMethod, currentStep) => {
  DataLayer.pushEvent({
    event: "gvCheckout",
    atfEcommerce: {
      ecommerce: {
        currencyCode: currency,
        cart: { id: cart.id },
        checkout: {
          //For express checkout, the currentStep is not passed and will be '2'
          actionField: {
            checkout_method: checkoutMethod,
            step: currentStep ? checkoutSteps[currentStep] : 2
          },
          products: cart.lineItems.map(
            (lineItem) => Mappers.mapClientModelToProductObject(lineItem, lineItem.quantity)
          )
        }
      }
    }
  });
};
const pushCheckoutNavigationEventToDataLayer = (cart, currentStep, currency) => {
  DataLayer.pushEvent({
    event: "Checkout",
    atfEcommerce: {
      ecommerce: {
        currencyCode: currency,
        cart: { id: cart.id },
        checkout: {
          actionField: { step: currentStep },
          products: cart.lineItems.map(
            (lineItem) => Mappers.mapClientModelToProductObject(lineItem, lineItem.quantity)
          )
        }
      }
    }
  });
};
const pushPurchaseEventToDataLayer = (order, currency, cartId, checkoutMethod, donation) => {
  const {
    rawLineItems,
    orderNumber,
    discountedAmount,
    totalPrice,
    shippingMethodPrice,
    subtotalAmount,
    taxAmount
  } = order;
  const discountTotal = discountedAmount;
  const discountCodes = order.discountCodes ? order.discountCodes.map((discountCode) => discountCode.code).join() : "";
  DataLayer.pushEcommerceEvent({
    event: "gvPurchase",
    atfEcommerce: {
      ecommerce: {
        currencyCode: currency,
        cart: { id: cartId ?? "" },
        purchase: {
          actionField: {
            id: orderNumber,
            revenue: calcPrice(totalPrice),
            tax: taxAmount && calcPrice(taxAmount),
            shipping: calcPrice(shippingMethodPrice),
            revenue_includes_tax: "true",
            original_revenue: calcPrice(subtotalAmount),
            discount_revenue: calcPrice(discountTotal),
            payment_method: order.paymentMethodKey,
            checkout_method: checkoutMethod || "regular_checkout",
            ...optionalKey("coupon", discountCodes),
            ...donation && {
              donation: calcPrice(donation.centAmount)
            }
          },
          products: mapOrderLineItemToDataLayerProductObjects(rawLineItems)
        }
      }
    }
  });
};
function mapOrderLineItemsToPurchaseRows(rawLineItems, orderNumber, currency) {
  const regularLineItems = rawLineItems.map((lineItem) => {
    return {
      currency,
      purchaseId: orderNumber,
      productId: lineItem.product.sku,
      sku: lineItem.product.sku,
      quantity: lineItem?.quantity || 1,
      unitPrice: (lineItem.price.unit.gross / 100).toFixed(2)
    };
  });
  return regularLineItems;
}
function trackOrderCompletionEvents(order, currency) {
  const batchEvents = [
    [`PaymentType__${order.paymentMethod}`],
    [`ShippingMode__${order.deliveryMethod}`]
  ];
  order?.lineItems.forEach((lineItem) => {
    const type = lineItem.productType;
    if (type) {
      batchEvents.push([`Product__${type}`]);
    }
  });
  order?.rawLineItems.forEach((lineItem) => {
    if ("configurationItems" in lineItem) {
      lineItem.configurationItems.forEach((configurationItem) => {
        batchEvents.push([`Lenses__${configurationItem.product.sku}`]);
      });
    }
  });
  monetate().trackBatchEvents(...batchEvents);
  monetate().trackPurchases(
    mapOrderLineItemsToPurchaseRows(order.rawLineItems, order.orderNumber, currency)
  );
}
const pushDonationSelectEventToDataLayer = (foundationKey, interactionType) => {
  DataLayer.pushEvent({
    event: "donations_amount_click",
    site_feature: "donations",
    interaction_type: interactionType ? calcPrice(interactionType) : void 0,
    foundation_key: foundationKey
  });
};
const getInteractionType = (paymentMethod) => {
  switch (paymentMethod) {
    case ExpressPaymentMethod.APPLE_PAY:
      return "express_applepay_init";
    case ExpressPaymentMethod.PAYPAL:
      return "express_paypal_init";
    case ExpressPaymentMethod.GOOGLE_PAY:
      return "express_googlepay_init";
    default:
      return "";
  }
};
const getCategory = (productType, lineItemType) => {
  switch (lineItemType) {
    case "sunglasses":
      return DataLayer.DataLayerTypes.Category.Sunglasses;
    case "contact-lenses":
      return DataLayer.DataLayerTypes.Category.ContactLenses;
    case "solutions":
      return DataLayer.DataLayerTypes.Category.Solutions;
    case "frames":
    case "non-prescription-bundle":
    case "prescription-glasses":
      return productType === "sunglasses" ? DataLayer.DataLayerTypes.Category.Sunglasses : DataLayer.DataLayerTypes.Category.Optical;
    case "ready-readers":
      return DataLayer.DataLayerTypes.Category.ReadyReaders;
    case "accessories":
      return DataLayer.DataLayerTypes.Category.Accessories;
    default:
      return "";
  }
};
const pushExpressCheckoutSubmitEventToDataLayer = ({
  cart,
  paymentMethod,
  isLoggedIn
}) => {
  const getBrand = (lineItem) => {
    const brand = lineItem.type == "prescription-glasses" ? lineItem.frame.brand.name : lineItem.brand.name;
    return brand || "";
  };
  const getSku = (lineItem) => {
    const sku = lineItem.type == "prescription-glasses" ? lineItem.frame.sku : lineItem.sku;
    return sku || "";
  };
  DataLayer.pushEvent({
    event: "express_checkout_confirm_click",
    site_feature: "express_checkout",
    interaction_type: getInteractionType(paymentMethod),
    session_status: isLoggedIn ? "logged_in" : "guest",
    items: cart.lineItems.map((item) => ({
      sku: getSku(item),
      quantity: `${item.quantity}`,
      brand: getBrand(item),
      category: getCategory(item.productType, item.type)
    })) || []
  });
};
const pushLoginExpressDeliveryOptionClickEventToDataLayer = ({
  shippingMethod,
  isLoggedIn
}) => {
  DataLayer.pushEvent({
    event: "login_checkout_interaction_click",
    site_feature: "delivery_option",
    interaction_type: `${shippingMethod}_click`,
    session_status: isLoggedIn ? "logged_in" : "guest",
    message_type: ""
  });
};
const pushLoginExpressEditUserDetailsClickEventToDataLayer = ({
  editType,
  isLoggedIn
}) => {
  DataLayer.pushEvent({
    event: "login_checkout_interaction_click",
    site_feature: "edit_details",
    interaction_type: `${editType}_click`,
    session_status: isLoggedIn ? "logged_in" : "guest",
    message_type: ""
  });
};
const pushLoginExpressRememberUserDetailsClickEventToDataLayer = ({
  isLoggedIn
}) => {
  DataLayer.pushEvent({
    event: "remember_details_click",
    site_feature: "edit_details",
    interaction_type: "remember_details_click",
    session_status: isLoggedIn ? "logged_in" : "guest",
    message_type: ""
  });
};
export {
  pushCheckoutEventToDataLayer,
  pushCheckoutNavigationEventToDataLayer,
  pushDonationSelectEventToDataLayer,
  pushExpressCheckoutSubmitEventToDataLayer,
  pushLoginExpressDeliveryOptionClickEventToDataLayer,
  pushLoginExpressEditUserDetailsClickEventToDataLayer,
  pushLoginExpressRememberUserDetailsClickEventToDataLayer,
  pushPurchaseEventToDataLayer,
  trackOrderCompletionEvents
};
//# sourceMappingURL=data-layer.js.map
